import { useEffect, useRef, useState } from "react";
import UiAvatarEditor from "react-avatar-editor";
import { connect, useDispatch } from "react-redux";
import { hideModal } from "../../../../../store/actions/modal";
import constants from "../../../../../constants";
import Modal from "../../../../Modal";
import defaultAvatarImage from "../../../../../assets/icons/avatar.svg";
import checkIcon from "../../../../../assets/check-bold.svg";
import Icon from "../../../../Icon";
import Spinner from "../../../../LogoLoader";

import {
  Avatar,
  AvatarEditorStyled,
  UserAvatar,
  UserAvatarContainer,
  AvatarCta,
  UpdatePicture,
  Cancel,
  SavePicture,
  Editor,
  Actions,
  ValidationError,
  Zoom,
  Rotate,
  Title,
  Subtitle,
  SuccessIcon,
} from "./../../styles";
import { UseFormSetValue } from "react-hook-form";
import Snackbar from "../../../../Snackbar";

const errorMessages = {
  extensionError: "Formatos permitidos .JPEG y .PNG",
  sizeError: "El peso de la imagen debe ser menor a 5Mb",
};

const isValidExtension = (event) => {
  const ext = event.target.files[0]?.name?.split(".").pop();
  return constants.userAvatar.validExtensions.includes(ext);
};

const isValidSize = (event) =>
  event.target.files[0].size / 1024 / 1024 < constants.userAvatar.maxSize;

interface AvatarEditorProps {
  picture: any;
  onSave: () => void;
  register: any;
  name: string;
  setValue: UseFormSetValue<any>
  errors: Record<string, any>;
  loading: boolean;
  changeUserAvatarStates: {
    loading: boolean,
    error: boolean | string,
    success: boolean
  }
}


const AvatarEditor = ({ changeUserAvatarStates, picture, onSave, register, name, setValue, errors, loading }: AvatarEditorProps) => {
  const dispatch = useDispatch();
  const avatarEditorRef = useRef<UiAvatarEditor | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const zoomInputRef = useRef<HTMLInputElement | null>(null);
  const degInputRef = useRef<HTMLInputElement | null>(null);

  const [userAvatar, setUserAvatar] = useState(defaultAvatarImage);
  const [editingImage, setEditingImage] = useState(null);
  const [zoom, setZoom] = useState(1.2);
  const [deg, setDeg] = useState(1);
  const [withError, setWithError] = useState<keyof typeof errorMessages | null>(null);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);

  const resetEdition = () => {
    setZoom(1);
    setEditingImage(null);
    setWithError(null);
    setStep(1);
    setSuccess(false);
  };

  const handlePictureChange = (event) => {
    const image = event.target.files[0];
    if (!isValidExtension(event)) return setWithError("extensionError");
    if (!isValidSize(event)) return setWithError("sizeError");
    setEditingImage(image);
    setWithError(null);
    setStep(2);
  };

  const handleCropConfirm = () => {
    if (!avatarEditorRef.current) return;
    const preview = avatarEditorRef.current.getImage().toDataURL();
    setUserAvatar(preview);
    setValue(name, preview);
    setStep(3);
  };

  const handleFinalSubmit = () => {
    if (onSave) onSave();
    // setSuccess(true);
    // setStep(4);
  };

  const handleCancel = () => {
    // Solo cerrar el modal si no estamos en el contexto de onboarding
    // El contexto de onboarding manejará el cierre cuando sea apropiado
    dispatch(hideModal());
    resetEdition();
  };

  useEffect(() => {
    if (picture) setUserAvatar(picture);
  }, [picture]);

  useEffect(() => {
    if (changeUserAvatarStates.success) {
      setSuccess(true);
      setStep(4);
      // No cerrar automáticamente el modal, dejar que el contexto de onboarding maneje el cierre
    }
  }, [changeUserAvatarStates])

  return (
    <>
      <Modal
        width="364"
        name="avatar-editor"
        onClose={() => handleCancel()}
        styles={{
          modalContent: {
            padding: 0
          }
        }}
      >
        <input
          type="file"
          id="file"
          hidden
          ref={inputRef}
          onChange={(e) => {
            handlePictureChange(e);
            register(name).onChange(e);
          }}
          onBlur={register(name).onBlur}
          accept="image/jpeg,image/png"
        />

        <input
          type="text"
          hidden
          id={name}
          {...register(name)}
          value={userAvatar}
        />

        <Editor>
          {step === 1 && (
            <>
              <UserAvatarContainer>
                <UserAvatar src={userAvatar} alt="Avatar preview" width="124px" height="124px" />
              </UserAvatarContainer>
              <div style={{ height: 28 }} />
              <Title>
                {userAvatar === defaultAvatarImage
                  ? "¡Todavía no tenés foto de perfil!"
                  : "Cambiar mi foto de perfil"}
              </Title>
              <div style={{ height: 8 }} />
              <Subtitle>
                Subí tu foto favorita y empezá a personalizar tu espacio.
              </Subtitle>
              <div style={{ height: 28 }} />
              <Actions>
                <UpdatePicture htmlFor="file">Subir mi foto</UpdatePicture>
                <Cancel
                  onClick={handleCancel}
                  options={{
                    type: "outline",
                    size: "md",
                    skin: "black"
                  }}
                >
                  Tal vez más tarde
                </Cancel>
              </Actions>
              <div style={{ height: 8 }} />
              {!!withError && (
                <ValidationError>{errorMessages[withError]}</ValidationError>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <AvatarEditorStyled
                ref={avatarEditorRef}
                image={editingImage}
                width={340}
                height={340}
                border={16}
                color={[255, 255, 255, 0.6]}
                scale={zoom}
                rotate={deg}
                borderRadius={360}
              />
              <div style={{ height: 28 }} />
              <Zoom>
                Zoom:
                <input
                  ref={zoomInputRef}
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.002}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
              </Zoom>
              <Rotate>
                Rotar:
                <input
                  ref={degInputRef}
                  type="range"
                  value={deg}
                  min={0}
                  max={180}
                  step={1}
                  onChange={(e) => setDeg(Number(e.target.value))}
                />
              </Rotate>
              <div style={{ height: 28 }} />
              <Actions>
                <SavePicture
                  onClick={handleCropConfirm}
                  disabled={changeUserAvatarStates.loading}
                  options={{
                    type: "filled",
                    size: "md",
                    skin: "purple"
                  }}
                >
                  Continuar
                </SavePicture>
                <Cancel
                  onClick={resetEdition}
                  disabled={changeUserAvatarStates.loading}
                  options={{
                    type: "outline",
                    size: "md",
                    skin: "black"
                  }}
                >
                  Elegir otra foto
                </Cancel>
              </Actions>
            </>
          )}

          {step === 3 && (
            <>
              <UserAvatarContainer>
                <UserAvatar src={userAvatar} alt="Avatar Preview" width="124px" height="124px" />
                {changeUserAvatarStates.loading && (
                  <span style={{
                    position: 'absolute',
                    width: 180,
                    height: 180,
                    top: -5,
                    left: "50%",
                    transform: "translate(-50%, 0px)",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Spinner size={180} />
                  </span>
                )}
              </UserAvatarContainer>
              <div style={{ height: 28 }} />
              <Title>¿Te gusta cómo se ve?</Title>
              <div style={{ height: 8 }} />
              <Subtitle>
                Tu foto se ve genial. ¿Querés subirla a tu perfil?
              </Subtitle>
              <div style={{ height: 28 }} />
              <Actions>
                <SavePicture
                  onClick={handleFinalSubmit}
                  disabled={changeUserAvatarStates.loading}
                  options={{
                    type: "filled",
                    size: "md",
                    skin: "purple"
                  }}
                >
                  Confirmar ✨
                </SavePicture>
                <Cancel
                  onClick={resetEdition}
                  disabled={changeUserAvatarStates.loading}
                  options={{
                    type: "outline",
                    size: "md",
                    skin: "black"
                  }}
                >
                  Elegir otra foto
                </Cancel>
              </Actions>
            </>
          )}

          {step === 4 && success && (
            <>
              <SuccessIcon>
                <Icon icon={checkIcon} size="55px" color="#4CA154" />
              </SuccessIcon>
              <div style={{ height: 28 }} />
              <Title>¡Perfecto! 🎉</Title>
              <div style={{ height: 8 }} />
              <Subtitle>
                Tu foto de perfil se subió correctamente. Puedes modificarla desde la sección de configuración.
              </Subtitle>
              <div style={{ height: 28 }} />
              <Actions>
                <SavePicture
                  onClick={handleCancel}
                  options={{
                    type: "outline",
                    size: "md",
                    skin: "black"
                  }}
                >
                  Finalizar
                </SavePicture>
              </Actions>
            </>
          )}
        </Editor>
      </Modal>

      <Snackbar
        visible={
          changeUserAvatarStates.success || !!changeUserAvatarStates.error
        }
        options={{
          time: 2000,
          type: changeUserAvatarStates.success ? "success" : "error",
        }}
      >
        {changeUserAvatarStates.success
          ? "Avatar actualizado."
          : "Error al actualizar el avatar."}
      </Snackbar>
    </>
  );
};


const state = ({ userStore }) => {
  const { states: changeUserAvatarStates } = userStore.changeUserAvatar;
  return {
    changeUserAvatarStates,
  };
};

export default connect(state)(AvatarEditor);
